import { defineStore } from "pinia"
import { ref, type Ref } from "vue"
import { LockRepository } from "@/repositories/lock-repository"
import { type ILockData } from "@/interfaces/socket/lock-data.interface"
import { BaseModel } from "@/models/base.model"
import GlobalsService from "@/services/globals-service"

export const useLockStore = defineStore("lockStore", () => {
  const locks: Ref<Map<string, string>> = ref(new Map<string, string>())

  function buildKey(model: BaseModel): string {
    return `${model.getClassName()}${model.id}`
  }

  function modelIsLockedLocal(model: BaseModel): boolean {
    return isLockedLocal(buildKey(model))
  }

  function getLockOwner(model: BaseModel): string | null {
    // Return the session UUID of the user who locked the model
    return locks.value.get(buildKey(model)) || null
  }

  function modelIsLockedByMe(model: BaseModel) {
    const mySessionUUID = GlobalsService.getInstance().getMySessionUUID()
    return getLockOwner(model) === mySessionUUID
  }

  function modelIsAccessibleByMe(model: BaseModel) {
    return !modelIsLockedLocal(model) || modelIsLockedByMe(model)
  }

  function lockModelLocal(model: BaseModel) {
    lockLocal(buildKey(model))
  }

  function releaseModelLocal(model: BaseModel) {
    releaseLocal(buildKey(model))
  }

  function isLockedLocal(key: string): boolean {
    return locks.value.has(key)
  }

  function lockLocal(key: string, sessionUUID?: string) {
    if (!sessionUUID) {
      sessionUUID = GlobalsService.getInstance().getMySessionUUID()
    }
    locks.value.set(key, sessionUUID)
  }

  function releaseLocal(key: string) {
    locks.value.delete(key)
  }

  async function fetchLocks() {
    const response = await new LockRepository().getLocks()
    const locks = response.data as { [modelType: string]: ILockData[] }
    for (const key of Object.keys(locks)) {
      for (const lock of locks[key]) {
        lockLocal(`${key}${lock.id}`, lock.sessionUuid)
      }
    }
  }

  return {
    locks,
    releaseLocal,
    lockLocal,
    fetchLocks,
    modelIsLockedByMe,
    modelIsAccessibleByMe,
    lockModelLocal,
    releaseModelLocal,
    getLockOwner
  }
})
